import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ProCard } from "@/components/ProCard";
import { ShieldCheck, MapPin, Users, TrendingUp, Search, Bug, ArrowRight, Check, HardHat, Wind, Hammer } from "lucide-react";
import { motion } from "framer-motion";

const HERO_IMG = "https://images.unsplash.com/photo-1686987537277-516791dabf61?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const nav = useNavigate();
  const [stats, setStats] = useState({ pros: 0, leads: 0 });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/businesses").then(({ data }) => setFeatured(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden hero-grid-bg">
        <div className="container-x pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider badge-ins px-3 py-1.5 rounded-full mb-6">
              <MapPin className="w-3.5 h-3.5" /> Sacramento County + 60-mile radius
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-[color:var(--navy)]">
              Find trusted local <span className="text-[color:var(--emerald)]">home-service</span> <span className="text-[color:var(--amber)]">pros</span>.
            </h1>
            <p className="text-lg text-[color:var(--ink-2)] mt-6 max-w-xl leading-relaxed">
              Home inspection, pest control, roofing, HVAC & general contractors — get matched with verified, licensed local pros in minutes. Free for homeowners, no spam, no shared bidding wars.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button onClick={() => nav("/quote")} data-testid="hero-request-quote-button" className="btn-amber rounded-full px-8 h-13 py-4 text-base font-semibold">
                Request a Free Quote <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button onClick={() => nav("/directory")} data-testid="hero-browse-directory-button" variant="outline" className="rounded-full px-8 py-4 h-13 text-base font-semibold border-slate-300">
                Browse the Directory
              </Button>
            </div>
            {stats.pros > 0 && <div className="flex items-center gap-8 mt-10" data-testid="hero-stats">
              <div><div className="font-display text-3xl font-extrabold text-[color:var(--navy)]">{stats.pros}+</div><div className="text-sm text-[color:var(--ink-3)]">Verified pros</div></div>
              <div className="w-px h-10 bg-slate-200" />
              <div><div className="font-display text-3xl font-extrabold text-[color:var(--navy)]">{stats.leads}+</div><div className="text-sm text-[color:var(--ink-3)]">Requests matched</div></div>
            </div>}
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img src={HERO_IMG} alt="Home inspector at work" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 hidden sm:block">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full btn-primary grid place-items-center"><ShieldCheck className="w-5 h-5 text-white" /></span>
                <div><div className="font-semibold text-sm text-[color:var(--navy)]">Verified & Licensed</div><div className="text-xs text-[color:var(--ink-3)]">Every pro is vetted</div></div>
              </div>
            </div>
            <div className="absolute -top-5 -right-5 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 hidden sm:block">
              <div className="text-xs text-[color:var(--ink-3)]">Avg. response</div>
              <div className="font-display font-extrabold text-xl text-[color:var(--emerald)]">under 1 hr</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {[
            { icon: Search, title: "Home Inspection", color: "var(--emerald)", items: ["Pre-purchase inspections", "Roof & foundation", "Sewer scope & mold"] },
            { icon: Bug, title: "Pest Control", color: "var(--amber)", items: ["Termite & wood-destroying", "Rodent & wildlife", "Bed bugs & recurring plans"] },
            { icon: HardHat, title: "Roofing", color: "#4338CA", items: ["Repair & replacement", "Leak & storm damage", "Gutters & skylights"] },
            { icon: Wind, title: "HVAC", color: "#0E7490", items: ["AC & heating repair", "Furnace & install", "Duct cleaning & tune-ups"] },
            { icon: Hammer, title: "General Contractors", color: "#9A3412", items: ["Remodels & additions", "Kitchen & bath", "Decks & repairs"] },
          ].map((s) => (
            <div key={s.title} className="card-soft p-6">
              <s.icon className="w-9 h-9 mb-4" style={{ color: s.color }} />
              <h3 className="font-display text-lg font-bold text-[color:var(--navy)]">{s.title}</h3>
              <ul className="mt-4 space-y-2">
                {s.items.map((i) => <li key={i} className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]"><Check className="w-4 h-4 text-[color:var(--emerald)] shrink-0" /> {i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section className="bg-[color:var(--bg-3)]">
        <div className="container-x py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-mono uppercase tracking-wider text-[color:var(--emerald)]">How it works</span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[color:var(--navy)] mt-2">Three steps to the right pro</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Tell us what you need", d: "Answer a few quick questions about your property and service." },
              { n: "02", t: "Get matched instantly", d: "We connect you with verified local pros in the Sacramento area." },
              { n: "03", t: "Compare & book", d: "Receive quotes, compare reviews, and hire with confidence." },
            ].map((s, i) => (
              <div key={s.n} className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="font-mono text-5xl font-bold text-emerald-200">{s.n}</div>
                <h3 className="font-display text-xl font-bold text-[color:var(--navy)] mt-3">{s.t}</h3>
                <p className="text-[color:var(--ink-2)] mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && <section className="container-x py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-mono uppercase tracking-wider text-[color:var(--emerald)]">Top rated</span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[color:var(--navy)] mt-2">Featured Sacramento pros</h2>
          </div>
          <Link to="/directory" className="hidden sm:inline-flex items-center gap-1 text-[color:var(--emerald)] font-semibold">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {featured.map((p, i) => <ProCard key={p.id} pro={p} index={i} onView={() => nav("/directory")} />)}
        </div>
      </section>}

      {/* PRO CTA */}
      <section className="container-x pb-8">
        <div className="rounded-3xl bg-[color:var(--navy)] text-white p-10 lg:p-16 relative overflow-hidden">
          <div className="hero-grid-bg absolute inset-0 opacity-20" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-emerald-400">For local pros</span>
              <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2">Grow your business with exclusive local leads</h2>
              <p className="text-slate-300 mt-4 max-w-lg">Join Sacramento's fastest-growing lead marketplace. Flat monthly plans or pay-per-lead credits — no long-term contracts, no shared bidding wars with 8 other companies.</p>
              <div className="flex gap-4 mt-8">
                <Button onClick={() => nav("/pricing")} data-testid="cta-view-pricing-button" className="btn-amber rounded-full px-8 font-semibold">View Membership Plans</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[{ icon: Users, t: "Exclusive leads", d: "Not shared with a crowd" }, { icon: TrendingUp, t: "Flexible pricing", d: "Subscribe or buy credits" }, { icon: ShieldCheck, t: "Verified badge", d: "Build instant trust" }, { icon: MapPin, t: "Local focus", d: "Sacramento + 60mi" }].map((c) => (
                <div key={c.t} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <c.icon className="w-6 h-6 text-emerald-400 mb-2" />
                  <div className="font-semibold">{c.t}</div>
                  <div className="text-sm text-slate-400">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
